import {
  classNames,
  contains,
  Icon,
  iconProps,
  useEventHandler,
  useGlobalId,
  useUpdateRef
} from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, {
  Children,
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { focusItem, handleChildCallback, noop } from './menu'

/**
 * @type {Record<string, import('./interfaces').FocusBehaviour>}
 */
export const FOCUS_BEHAVIOURS = {
  FOCUS_FIRST_ITEM: 'focusFirstItem',
  NO_AUTO_FOCUS: 'noAutoFocus'
}
/**
 * @param {import('./interfaces').UseMenuItemProps} props
 * @param {import('react').Ref} ref
 */
export function useNavMenuItem (
  {
    autoFocusBehaviour,
    children,
    className,
    classPrefix,
    href,
    iconClass,
    iconName,
    iconProps,
    isMenuOpen,
    isOpenOnHover,
    isSelected,
    onClick,
    onClose,
    onFocus,
    onItemKeyDown,
    onKeyDown,
    onOpen,
    onSelect,
    orientation,
    renderLabel,
    target,
    withAutoOpen
  },
  ref
) {
  /** @type {import('react').RefObject<HTMLElement>} */
  const labelRef = useRef()
  /** @type {import('react').RefObject<HTMLElement>} */
  const elementRef = useRef()
  /** @type {import('react').RefObject<import('react').RefObject<HTMLElement>[]|HTMLElement>} */
  const navItemsRef = useRef([])
  const labelId = useGlobalId(`${classPrefix}-`)
  const isOpenByKeyboard = useRef(false)
  const prevIsMenuOpen = useRef(isMenuOpen)
  const hasMenuItems = useMemo(() => hasMenu(children), [children])
  const labelClass = `${classPrefix}-label`
  const mouseEventHandler =
    isOpenOnHover && hasMenuItems ? handleMouseEvent : null
  const focus = useCallback(function focus (index) {
    if (index == null) {
      focusItem(labelRef)
    } else if (!isNaN(index)) {
      const allNavItems = navItemsRef.current
      if (index === -1) {
        focusItem(allNavItems[allNavItems.length - 1])
      } else {
        focusItem(allNavItems[index])
      }
    }
  }, [])
  useUpdateRef(ref, elementRef, { focus })
  useEffect(componentDidUpdate, [isMenuOpen])
  useEventHandler('click', handleDocumentClick)

  const content = (
    <li
      className={classNames(
        classPrefix,
        { [`${classPrefix}-is-selected`]: isSelected },
        className
      )}
      ref={elementRef}
      onMouseEnter={mouseEventHandler}
      onMouseLeave={mouseEventHandler}
      onClick={onClick}
      role='none'
    >
      {getMenuItem()}
      {hasMenuItems
        ? React.Children.toArray(children)
          .filter(childIsMenu)
          .map(createMenu)
        : null}
    </li>
  )

  return { content, navItemsRef, hasMenuItems, isOpenByKeyboard, focus }

  function getMenuItem () {
    if (hasMenuItems) {
      return (
        <span
          className={labelClass}
          tabIndex='0'
          aria-haspopup={hasMenuItems}
          aria-current={isSelected ? 'page' : null}
          aria-expanded={isMenuOpen}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          id={labelId}
          ref={labelRef}
          role='menuitem'
        >
          {React.Children.toArray(children).filter(
            child => !childIsMenu(child)
          )}

          <Icon
            className={iconClass}
            name={iconName}
            title={isMenuOpen ? 'Collapse' : 'Expand'}
            aria-hidden='true'
            iconProps={iconProps}
          />
        </span>
      )
    }
    return renderLabel({
      children,
      className: labelClass,
      role: 'menuitem',
      'aria-current': isSelected ? 'page' : null,
      href,
      target,
      onFocus,
      onKeyDown,
      onSelect,
      ref: labelRef
    })
  }

  /**
   * @param {MouseEvent} event
   */
  function handleDocumentClick ({ target }) {
    const element = elementRef.current
    const labelElement = labelRef.current
    const isTargetWithinNavItem =
      target === element ||
      target === labelElement ||
      contains(labelElement, target)

    if (isTargetWithinNavItem && hasMenuItems) {
      if (isMenuOpen) {
        onClose()
      } else {
        onOpen()
      }
    } else if (isMenuOpen && !contains(element, target)) {
      !withAutoOpen && onClose()
    }
  }

  function componentDidUpdate () {
    const shouldAutoFocus =
      isOpenByKeyboard.current ||
      autoFocusBehaviour === FOCUS_BEHAVIOURS.FOCUS_FIRST_ITEM

    if (shouldAutoFocus && prevIsMenuOpen.current === false && isMenuOpen) {
      const menuRef =
        navItemsRef.current && Array.isArray(navItemsRef.current)
          ? navItemsRef.current[0]
          : navItemsRef

      menuRef && menuRef.current && menuRef.current.focus(0)
      isOpenByKeyboard.current = false
    }
    prevIsMenuOpen.current = isMenuOpen
  }

  /**
   * @param {import('react').ReactElement} child
   * @param {number} index
   */
  function createMenu (child, index) {
    navItemsRef.current[index] = navItemsRef.current[index] || createRef()
    const ref =
      navItemsRef.current[index]

    return React.cloneElement(child, {
      isPositioned: true,
      isOpen: isMenuOpen,
      onItemKeyDown,
      onItemSelect: handleChildCallback(onSelect, child.props.onItemSelect),
      ref,
      orientation,
      withAutoOpen
    })
  }

  /**
   * @param {import('react').MouseEvent} event
   */
  function handleMouseEvent ({ type }) {
    if (type === 'mouseenter') {
      onOpen()
    } else {
      onClose()
    }
  }
}

/**
 * @param {import('react').ReactNode} children
 */
function hasMenu (children) {
  return Children.toArray(children).some(childIsMenu)
}

/**
 * @param {import('react').ReactNode} children
 */
function childIsMenu (child) {
  return (
    child != null &&
    child.type != null &&
    child.type.componentName &&
    child.type.componentName.slice(0, 6) === 'zb.Nav'
  )
}

/**
 * @param {import('react').RefObject<HTMLElement>[]|HTMLElement} navItems
 */
export function getMenu (navItems) {
  if (!navItems) return navItems
  return Array.isArray(navItems)
    ? navItems[0] && navItems[0].current
    : navItems
}

/**
 * Create a React Nav Menu Item component
 *
 * @param {import('./interfaces').NavMenuItemProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function navMenuItem (
  { isMenuOpen, onKeyDown, onOpen, onClose, onSelect, orientation, ...rest },
  ref
) {
  const {
    content,
    focus,
    hasMenuItems,
    isOpenByKeyboard,
    navItemsRef
  } = useNavMenuItem(
    {
      ...rest,
      iconName: 'chev-right-xsmall',
      isMenuOpen,
      onClose,
      onItemKeyDown: handleItemKeydown,
      onKeyDown: handleKeyDown,
      onOpen,
      onSelect,
      orientation
    },
    ref
  )
  const isVertical = orientation !== 'horizontal'

  return content

  /**
   * @param {import('react').KeyboardEvent} event
   */
  function handleKeyDown (event) {
    const { key } = event

    switch (key) {
      case 'Enter':
        if (hasMenuItems) {
          if (isMenuOpen) {
            onClose()
          } else {
            isOpenByKeyboard.current = true
            onOpen()
          }
          event.preventDefault()
        } else {
          onSelect(event)
        }
        break
      case 'Escape':
        if (hasMenuItems && isMenuOpen) {
          onClose()
        }
        break
      case 'ArrowDown': // falls through
      case 'ArrowRight':
        if (hasMenuItems && isMenuOpen) {
          if (isVertical ? key === 'ArrowRight' : key === 'ArrowDown') {
            const menu = getMenu(navItemsRef.current)
            menu && menu.focus(0)
          }
          event.preventDefault()
        }
        break
    }

    onKeyDown(event)
  }

  /**
   * @type {import('./interfaces').OnItemKeyDown}
   */
  function handleItemKeydown (event, groupIndex, focusedIndex) {
    const { key } = event
    switch (key) {
      case 'Escape':
        if (isMenuOpen) {
          onClose()
          focus()
        }
        break
      case 'ArrowUp': // falls through
      case 'ArrowLeft':
        if (
          isMenuOpen && isVertical ? key === 'ArrowLeft' : key === 'ArrowUp'
        ) {
          onClose()
          focus()
          event.preventDefault()
        }
        break
    }
  }
}

const NavMenuItem = forwardRef(navMenuItem)

NavMenuItem.displayName = 'NavMenuItem'

NavMenuItem.propTypes = {
  href: PropTypes.string,
  target: PropTypes.string,
  className: PropTypes.string,
  isMenuOpen: PropTypes.bool,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  onSelect: PropTypes.func,
  renderLabel: PropTypes.func,
  isOpenOnHover: PropTypes.bool,
  autoFocusBehaviour: PropTypes.oneOf(Object.values(FOCUS_BEHAVIOURS)),
  iconProps,
  iconClass: PropTypes.string.isRequired,
  classPrefix: PropTypes.string.isRequired,
  withAutoOpen: PropTypes.bool
}

NavMenuItem.defaultProps = {
  renderLabel: ({ onSelect, ...props }) => <a {...props} onClick={onSelect} />,
  autoFocusBehaviour: FOCUS_BEHAVIOURS.FOCUS_FIRST_ITEM,
  onClose: noop,
  onOpen: noop,
  onKeyDown: noop,
  iconClass: 'zb-nav-menu-item-open-icon',
  classPrefix: 'zb-nav-menu-item'
}

// Workaround to allow type comparison if types are wrapped
// See: https://github.com/gaearon/react-hot-loader/issues/304
NavMenuItem.componentName = 'zb.NavMenuItem'

export default NavMenuItem
